import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { gql, GraphQLClient } from '@tf/api';
import { createObjectAliasId, getProfileExternalId, T } from '@tf/utils';

import { useClusterId, useTenantId } from '@/core/appMeta';

// TODO: ask, do we need archetype here to narrow search?
export const useObjectIdByAlias = (archetype: string, alias: string) => {
	const tenantId = useTenantId();
	T.assertNotNullish(tenantId, 'no tenantId in AppMeta');

	const clusterId = useClusterId();
	const fullAliasId = createObjectAliasId({ alias, tenantId, clusterId });

	return useQuery({
		queryKey: ['objectIdyAlias', archetype, alias],
		queryFn: async () => {
			const res = await GraphQLClient.getInstance().request(
				gql`
					query ObjectIdyAlias($aliasId: ID!, $archetype: String!) {
						get(rootFilter: { id: $aliasId, archetype: $archetype }) {
							id
						}
					}
				`,
				{ aliasId: fullAliasId, archetype }
			);
			return res.get[0].id;
		},
	});
};

export const useUpdateProfileExternalId = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ objectId, value }: { objectId: string; value: string }) => {
			const aliasesResponse = await GraphQLClient.getInstance().request<
				{ get: { aliases: Record<string, string> }[] },
				{ id: string }
			>(
				gql`
					query GetProfileAliases($id: ID!) {
						get(rootFilter: { id: $id }) {
							aliases
						}
					}
				`,
				{ id: objectId }
			);

			const externalId = getProfileExternalId(aliasesResponse.get[0]?.aliases ?? {});

			if (externalId === undefined) {
				await GraphQLClient.getInstance().request(
					gql`
						mutation AddProfileExternalId($objectId: ID!, $value: JSONPointer!, $labelValue: JSON!) {
							mutate(
								patch: [
									{
										objectId: $objectId
										objectAliasesPatch: [{ op: "add", path: $value, value: "@externalId" }]
										objectLabelsPatch: [{ op: "add", path: "/external_id", value: $labelValue }]
									}
								]
							) {
								id
								aliases
							}
						}
					`,
					{
						objectId,
						value: `/${value}`,
						labelValue: value,
					}
				);

				// Temporary solution: Perform a second request to update the alias for new aliases
				// to the same value to work around a backend issue
				//
				// TODO: Remove this when the backend is fixed

				await GraphQLClient.getInstance().request(
					gql`
						mutation RemoveProfileExternalId($objectId: ID!, $value: JSONPointer!) {
							mutate(
								patch: [{ objectId: $objectId, objectAliasesPatch: [{ op: "remove", path: $value }] }]
							) {
								id
								aliases
							}
						}
					`,
					{
						objectId,
						value: `/${value}`,
					}
				);

				return await GraphQLClient.getInstance().request(
					gql`
						mutation AddProfileExternalId($objectId: ID!, $value: JSONPointer!) {
							mutate(
								patch: [
									{
										objectId: $objectId
										objectAliasesPatch: [{ op: "add", path: $value, value: "@externalId" }]
									}
								]
							) {
								id
								aliases
							}
						}
					`,
					{
						objectId,
						value: `/${value}`,
					}
				);
			} else {
				return await GraphQLClient.getInstance().request(
					gql`
						mutation UpdateProfileExternalId(
							$objectId: ID!
							$value: JSONPointer!
							$prevValue: JSONPointer!
							$labelValue: JSON!
						) {
							mutate(
								patch: [
									{
										objectId: $objectId
										objectAliasesPatch: [
											{ op: "remove", path: $prevValue }
											{ op: "add", path: $value, value: "@externalId" }
										]
										objectLabelsPatch: [{ op: "add", path: "/external_id", value: $labelValue }]
									}
								]
							) {
								id
								aliases
							}
						}
					`,
					{
						objectId,
						value: `/${value}`,
						prevValue: `/${externalId}`,
						labelValue: value,
					}
				);
			}
		},
		onSuccess: (_, { objectId }) => {
			queryClient.invalidateQueries(profileExternalIdOptions(objectId));
		},
	});
};

const profileExternalIdOptions = (objectId: string) =>
	queryOptions({
		queryKey: ['external-id', objectId],
		queryFn: async () => {
			return GraphQLClient.getInstance().request<
				{ get: { aliases: Record<string, string> }[] },
				{ id: string }
			>(
				gql`
					query GetProfileAliases($id: ID!) {
						get(rootFilter: { id: $id }) {
							aliases
						}
					}
				`,
				{ id: objectId }
			);
		},
		select: ({ get }) => getProfileExternalId(get[0]?.aliases ?? {}),
	});

export const useProfileExternalId = (objectId: string) => {
	return useQuery(profileExternalIdOptions(objectId));
};
